import { createContext, ReactNode, useContext, useRef } from 'react';

/** A promise chain several autosave hooks can share, so their writes never overlap. */
export type SettingsSaveQueue = { current: Promise<void> };

const SettingsSaveQueueContext = createContext<SettingsSaveQueue | undefined>(undefined);

/**
 * One save queue for a page whose sections write the same record.
 *
 * `updateAllOrgSettings` merges onto the settings it read at the start of the request and returns
 * the whole record, which the query cache then replaces wholesale. Two sections saving at once is
 * therefore a lost update: the response that lands last carries the other section's pre-edit
 * value, the hook adopts it as what the server holds, and that section's next save writes the
 * stale value back. The Workflow page has had two sections since Modifiers moved onto it, and
 * blurring a project description by clicking a modifier toggle is enough to overlap them
 * (KAIDEV-4345 review).
 *
 * Serializing is what makes each response current: every write starts after the previous one has
 * landed, so the record it merges onto already contains every earlier edit. A page with one
 * section needs no provider - the hook falls back to a queue of its own.
 *
 * This orders the writes made through it, and nothing else. A refetch landing with a pre-save
 * answer is outside it: `useSettingsAutosave` holds its own check on the merge base for the
 * window between a save and its echo, and closing the rest needs a recency signal from the query
 * cache itself (KAIDEV-4362). Two admins, or two tabs, writing different sub-records at once is
 * settled by neither: that remains last-write-wins per sub-record.
 */
export const SettingsSaveQueueProvider = ({ children }: { children: ReactNode }) => {
  const queue = useRef<Promise<void>>(Promise.resolve());
  return <SettingsSaveQueueContext.Provider value={queue}>{children}</SettingsSaveQueueContext.Provider>;
};

/** The page's shared queue, or `undefined` when this hook is the only writer. */
export const useSharedSettingsSaveQueue = (): SettingsSaveQueue | undefined => useContext(SettingsSaveQueueContext);
